import React from "react";
import {Card, Box, Divider, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";

import SlidingSessionHeader from "./SlidingSessionHeader";
import SlidingSessionTimer from "./SlidingSessionTimer";
import ChildAssessmentStrip from "../../sessions/ChildAssessmentStrip";
import SessionContentDetails from "../../sessions/SessionContentDetails";
import LaunchMeetingButton from "../../sessions/LaunchMeetingButton";
import SessionMemberDetails from "../SessionMemberDetails";
import {useSessionTimer} from "../../../hooks/useSessionTimer";
import {useSessionStatus} from "../../../hooks/useSessionStatus";
import {useSessionLaunch} from "../../../hooks/useSessionLaunch";
import UnbookedTimeslotDetails from "../UnbookedTimeslotDetails";

const FIRST_BOOKING_ID = 0;

const styles = {
    card: {
        container: (isSelected) => ({
            width: {
                xs: "32rem",
                xl: "36rem"
            },
            height: "auto",
            display: "flex",
            flexDirection: "column",
            backgroundColor: isSelected ? grey[100] : 'white',
        })
    },
    typography: {
        contentTitle: {
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "flex",
            justifyContent: 'flex-start',
            marginBottom: "0.25rem"
        }
    },
    section: {
        contentBox: {
            display: "flex",
            justifyContent: "space-between",
            marginLeft: "0.5rem"
        },
        memberBox: {
            display: "flex",
            justifyContent: "space-between",
            margin: "0.5rem",
            marginTop: 0
        },
        memberDetailsWrapper: {
            width: {
                xs: "25rem",
                xl: "29rem"
            }
        },
        sessionContentWrapper: {
            minHeight: "10.7rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-evenly"
        }
    }
};

export default function SlidingSessionCard({
                                               selectedEvent,
                                               serviceTypeDetails,
                                               currentTime,
                                               onCardClose,
                                               onPdfClick,
                                               onExpandContent,
                                               isSelected = false
                                           }) {
    const {hours, minutes, seconds} = useSessionTimer(selectedEvent, currentTime);
    const {sessionStatusInfo, isSessionStarted, isSessionEnded, canLaunchSession} = useSessionStatus(selectedEvent, currentTime);
    const {handleSessionLaunch} = useSessionLaunch(selectedEvent);

    const hasSession = !!selectedEvent?.isTeachingSession;
    const isZoomLinkCreated = Boolean(selectedEvent?.zoomMeetingUrl);
    const showBookingAssessmentStrip = selectedEvent?.contents?.some(content => content.isAssessed);
    const bookingId = selectedEvent?.seats?.[FIRST_BOOKING_ID]?.booking?.id;
    const contents = selectedEvent?.contents || [];

    if (!selectedEvent) {
        return null;
    }

    return (
        <Card sx={styles.card.container(isSelected)}>
            <SlidingSessionHeader
                selectedEvent={selectedEvent}
                serviceTypeDetails={serviceTypeDetails}
                handleHeaderClose={onCardClose}
                hasNoSession={!hasSession}/>
            <Divider/>

            {
                hasSession
                &&
                (
                    <Box sx={styles.section.sessionContentWrapper}>
                        {
                            contents
                            &&
                            contents.length > 0
                            &&
                            (
                                <Box sx={styles.section.contentBox}>
                                    <Box>
                                        <Typography sx={styles.typography.contentTitle} variant="body2">Content</Typography>
                                        <SessionContentDetails
                                            contentInfos={contents}
                                            onPdfClick={onPdfClick}
                                            onExpandContent={onExpandContent}/>
                                    </Box>
                                    <SlidingSessionTimer
                                        hours={hours}
                                        minutes={minutes}
                                        seconds={seconds}
                                        sessionStatusInfo={sessionStatusInfo}
                                        isSessionStarted={isSessionStarted}
                                        isSessionEnded={isSessionEnded}/>
                                </Box>
                            )
                        }
                        <Box sx={styles.section.memberBox}>
                            <Box sx={styles.section.memberDetailsWrapper}>
                                <SessionMemberDetails
                                    selectedSessionEvent={selectedEvent}
                                    serviceType={serviceTypeDetails?.type}
                                    showHistory={false}/>
                            </Box>
                            <LaunchMeetingButton
                                canLaunchSession={canLaunchSession}
                                isZoomLinkCreated={isZoomLinkCreated}
                                handleSessionLaunch={handleSessionLaunch}
                                showCompactButton={true}/>
                        </Box>
                    </Box>
                )
            }
            {
                !hasSession
                &&
                <UnbookedTimeslotDetails selectedEvent={selectedEvent} compactView={true}/>
            }
            {
                showBookingAssessmentStrip
                &&
                bookingId
                &&
                (
                    <>
                        <Divider/>
                        <ChildAssessmentStrip bookingId={bookingId} compact={true}/>
                    </>
                )
            }
        </Card>
    );
}
